import React, { useEffect, useId, useRef } from 'react'
import { createPortal } from 'react-dom'
import { IconShield } from '../icon/icon'
import '../sso-workspace/sso-workspace.scss'
import '../../layout/authorized/org/api-keys/api-keys.scss'

// The frame of every reveal-once dialog (SCIM token, API key, publish key):
// a title, a body of numbered sections, a short note with an icon, and a
// footer with the one primary button. Escape and the backdrop don't close
// it, so the secret is never lost by a stray key or click. Wide on a desktop;
// a full-screen sheet with a sticky header and footer on a phone (api-keys.scss).
// `children` is the body; `focus` is 'done' (the button) or 'first' (the
// Copy button in the body).
//
// The page behind is dimmed, in both themes (owner 2026-10-29: it showed at
// full brightness around the dialog), by the workspace's scrim (.sw-scrim,
// sso-workspace.scss; the drawers' backdrop is lighter on purpose), and the
// note is in the warn tone: it matters, but nothing has gone wrong.
// `subtitle` names what the secret is for (the SCIM dialogs: the connection)
// under the title.
export const SecretDialog = ({ title, subtitle, backdropTestId, note, doneLabel = "I've stored it", onDone, focus = 'done', children }) => {
  const titleId = useId()
  const doneRef = useRef(null)
  const bodyRef = useRef(null)

  useEffect(() => {
    const el = focus === 'first' && bodyRef.current ? bodyRef.current.querySelector('button[aria-label^="Copy"]') : doneRef.current
    if (el) el.focus({ preventScroll: true })
  }, [focus])

  return createPortal(
    <div className="sw-drawer sw-scrim justify-content-center align-items-center" data-testid={backdropTestId}>
      <div className="sw-dialog ak-secret-dialog ak-secret-dialog-lg" role="dialog" aria-modal="true" aria-labelledby={titleId}
        onKeyDown={e => { if (e.key === 'Escape') e.stopPropagation() }}>
        <header className="ak-dialog-head">
          <h2 className="h5 m-0" id={titleId}>{title}</h2>
          {subtitle && <p className="sd-subtitle m-0 mt-1" data-testid="secret-subtitle">{subtitle}</p>}
        </header>
        <div className="ak-dialog-body" ref={bodyRef}>
          {note && <p className="ak-note" data-testid="secret-note"><IconShield className="ak-note-icon" /> <span>{note}</span></p>}
          {children}
        </div>
        <footer className="sw-dialog-actions ak-dialog-foot">
          <button type="button" className="btn sw-btn-primary" ref={doneRef} onClick={onDone}>{doneLabel}</button>
        </footer>
      </div>
    </div>,
    document.body
  )
}

// A numbered section of the body.
export const SecretSection = ({ n, title, children }) =>
  <section className="ak-section">
    <h3 className="ak-section-title">{n ? `${n}. ` : ''}{title}</h3>
    {children}
  </section>
